import { Kanban } from '@phosphor-icons/react';
import i18next from 'i18next';
import React from 'react';
//import { IconNavLightBulb } from '../../../NavigationV2/NavigationIcons';

const ns = 'external-operations-navigation';

const t = (key: string) => i18next.t(key, { ns });

export const getQuickNavigation = (
  pages: NavigationPage[],
  list: NavigationPage[] = [],
): NavigationPage[] => {
  pages.forEach((page) => {
    if (page.quickNav) {
      list.push(page);
    }
    if (page.pages) {
      getQuickNavigation(page.pages, list);
    }
  });
  return list;
};

interface NavigationPageQuickNav {
  title?: string;
  activeUrl: string;
  fallbackActiveUrls?: string[];
  isActive?: boolean;
  resolveNotificationsPath?: string;
  resolveNotifications?: boolean;
  shouldCloseMobileMenu?: boolean;
}

export interface NavigationPage {
  title: string;
  description?: string;
  splash?: {
    description: string;
  };
  url: string;
  externalUrl?: boolean;
  activeUrl?: string;
  fallbackActiveUrls?: string[];
  root?: boolean;
  exact?: boolean;
  quickNav?: NavigationPageQuickNav;
  isActive?: boolean;
  pages?: NavigationPage[];
  icon?: React.ReactNode;
  splashImage?: string;
  availability: {
    hidden?: boolean;
  };
}

export const getActiveNavigationTree = (
  path: string,
  pages: NavigationPage[],
): NavigationPage[] => {
  pages.forEach((page) => {
    let isActiveChild = false;

    // If the page has child pages, recurse into them first
    if (page.pages) {
      // Recurse into child pages and determine if any child is active
      const childPages = getActiveNavigationTree(path, page.pages);
      isActiveChild = childPages.some((childPage) => childPage.isActive);
    }
    // Set isActive to true if the page's url starts with the given path or if any child is active

    if (
      page.exact
        ? path === page.url
        : (path.split('/').length > 1 &&
            (path.startsWith(page.url) ||
              page.fallbackActiveUrls?.some((x) => path.startsWith(x)))) ||
          isActiveChild
    ) {
      page.isActive = true;
    } else {
      // Optionally, explicitly set isActive to false if it should not inherit a previous true state
      // This is useful if you are re-using the function on the same pages array multiple times
      page.isActive = false;
    }

    if (
      page.quickNav?.activeUrl &&
      (page.root
        ? path === page.quickNav.activeUrl
        : path.startsWith(page.quickNav.activeUrl) ||
          page.quickNav.fallbackActiveUrls?.some((y) => path.startsWith(y)))
    ) {
      page.quickNav.isActive = true;
    }
  });
  return pages;
};

export const getNavigationMenu = (
  path: string,
  pages: NavigationPage[],
): NavigationPage[] | undefined => {
  return pages.find((x) => x.url.startsWith(path))?.pages;
};

export const getBreadcrumbs = (pages: NavigationPage[]): NavigationPage[] => {
  return pages.reduce((prev: NavigationPage[], curr: NavigationPage) => {
    if (curr.isActive) {
      // Add the current page if it's active
      prev.push(curr);
      // If there are subpages and they are active, recursively get their breadcrumbs and flatten them into the array
      if (curr.pages) {
        prev.push(...getBreadcrumbs(curr.pages));
      }
    }
    return prev;
  }, []);
};

export const getNavigationTree = (path: string): NavigationPage[] => {
  const tree: NavigationPage[] = [
    /*
    {
      title: t('pages.dashboard.title'),
      description: t('pages.dashboard.description'),
      url: '/',
      root: true,
      exact: true,
      quickNav: {
        activeUrl: '/external',
        shouldCloseMobileMenu: true,
      },
      icon: <IconNavLightBulb />,
      availability: {},
    },
    */
    {
      title: t('pages.operations.title'),
      splash: {
        description: t('pages.operations.description'),
      },
      url: '/operations',
      icon: <Kanban size="1em" weight="light" />,
      availability: {},
      pages: [
        {
          title: t('pages.operations.pages.work-orders.title'),
          description: t('pages.operations.pages.work-orders.description'),
          url: '/operations/work-orders',
          quickNav: {
            title: t('pages.operations.title'),
            activeUrl: '/operations',
          },
          icon: <Kanban size="1em" weight="light" />,
          availability: {},
        },
        {
          title: t('pages.operations.pages.stations.title'),
          description: t('pages.operations.pages.stations.description'),
          url: '/operations/stations',
          icon: <Kanban size="1em" weight="light" />,
          availability: {},
        },
      ],
    },
  ];

  const reduce = (nt: NavigationPage[]) => {
    return nt.reduce((prev, curr) => {
      if (!curr.availability.hidden) {
        prev.push({
          ...curr,
          pages: curr.pages ? reduce(curr.pages) : undefined,
        });
      }
      return prev;
    }, [] as NavigationPage[]);
  };

  const reducesTree = reduce(tree);
  return getActiveNavigationTree(path, reducesTree);
};
